import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { companySettingsAPI } from '../services/api';
import { 
  Building2, 
  Upload, 
  Trash2, 
  Save,
  Loader2,
  Image as ImageIcon
} from 'lucide-react';

const CompanySettings = () => {
  const [settings, setSettings] = useState({
    companyName: '',
    address: '',
    postalCity: '',
    iban: '',
    kvk: '',
    btw: '',
    email: '',
    phone: ''
  });
  const [logo, setLogo] = useState(null);
  const [logoFilename, setLogoFilename] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const [settingsRes, logoRes] = await Promise.all([
        companySettingsAPI.get(),
        companySettingsAPI.getLogo()
      ]);
      
      setSettings({
        companyName: settingsRes.data.companyName || '',
        address: settingsRes.data.address || '',
        postalCity: settingsRes.data.postalCity || '',
        iban: settingsRes.data.iban || '',
        kvk: settingsRes.data.kvk || '',
        btw: settingsRes.data.btw || '',
        email: settingsRes.data.email || '',
        phone: settingsRes.data.phone || ''
      });
      
      if (logoRes.data.logo) {
        setLogo(logoRes.data.logo);
        setLogoFilename(logoRes.data.filename);
      }
    } catch (error) {
      console.error('Fout bij ophalen instellingen:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      await companySettingsAPI.update(settings);
      toast({
        title: "Gelukt",
        description: "Bedrijfsinstellingen opgeslagen"
      });
    } catch (error) {
      toast({
        title: "Fout",
        description: "Kan instellingen niet opslaan",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Fout",
        description: "Ongeldig bestandstype. Gebruik PNG, JPG of WEBP",
        variant: "destructive"
      });
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: "Fout",
        description: "Bestand te groot. Maximum 2MB",
        variant: "destructive"
      });
      return;
    }

    try {
      setUploadingLogo(true);
      await companySettingsAPI.uploadLogo(file);
      
      // Refresh logo
      const logoRes = await companySettingsAPI.getLogo();
      setLogo(logoRes.data.logo);
      setLogoFilename(logoRes.data.filename);
      
      toast({
        title: "Gelukt",
        description: "Logo succesvol geüpload"
      });
    } catch (error) {
      toast({
        title: "Fout",
        description: error.response?.data?.detail || "Kan logo niet uploaden",
        variant: "destructive"
      });
    } finally {
      setUploadingLogo(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteLogo = async () => {
    try {
      await companySettingsAPI.deleteLogo();
      setLogo(null);
      setLogoFilename(null);
      toast({
        title: "Gelukt",
        description: "Logo verwijderd"
      });
    } catch (error) {
      toast({
        title: "Fout",
        description: "Kan logo niet verwijderen",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Logo Section */}
      <Card className="shadow-md border-0">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center text-lg">
            <ImageIcon className="w-5 h-5 mr-2 text-blue-600" />
            Bedrijfslogo
          </CardTitle>
          <p className="text-sm text-gray-500">
            Dit logo wordt gebruikt op offertes en facturen
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex items-start space-x-6">
            {/* Logo Preview */}
            <div className="w-48 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
              {logo ? (
                <img 
                  src={logo} 
                  alt="Bedrijfslogo" 
                  className="max-w-full max-h-full object-contain"
                />
              ) : (
                <div className="text-center text-gray-400">
                  <ImageIcon className="w-10 h-10 mx-auto mb-2" />
                  <p className="text-sm">Geen logo</p>
                </div>
              )}
            </div>

            {/* Upload Controls */}
            <div className="space-y-3">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleLogoUpload}
                accept="image/png,image/jpeg,image/jpg,image/webp"
                className="hidden"
              />
              
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingLogo}
                className="w-full"
              >
                {uploadingLogo ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploaden...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    {logo ? 'Logo Vervangen' : 'Logo Uploaden'}
                  </>
                )}
              </Button>

              {logo && (
                <Button
                  variant="outline"
                  onClick={handleDeleteLogo}
                  className="w-full text-red-600 hover:bg-red-50 border-red-200"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Logo Verwijderen
                </Button>
              )}

              <p className="text-xs text-gray-500">
                PNG, JPG of WEBP. Max 2MB.
                <br />
                Aanbevolen: 300x100 pixels
              </p>

              {logoFilename && (
                <p className="text-xs text-gray-400">
                  Huidig bestand: {logoFilename}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Company Details Section */}
      <Card className="shadow-md border-0">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center text-lg">
            <Building2 className="w-5 h-5 mr-2 text-green-600" />
            Bedrijfsgegevens
          </CardTitle>
          <p className="text-sm text-gray-500">
            Deze gegevens worden getoond op offertes en facturen
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Bedrijfsnaam</Label>
              <Input
                value={settings.companyName}
                onChange={(e) => setSettings({ ...settings, companyName: e.target.value })}
                placeholder="Van Vessem Sound & Lights"
              />
            </div>

            <div className="space-y-2">
              <Label>E-mailadres</Label>
              <Input
                type="email"
                value={settings.email}
                onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                placeholder="info@voorbeeld.nl"
              />
            </div>

            <div className="space-y-2">
              <Label>Adres</Label>
              <Input
                value={settings.address}
                onChange={(e) => setSettings({ ...settings, address: e.target.value })}
                placeholder="Straatnaam 123"
              />
            </div>

            <div className="space-y-2">
              <Label>Postcode & Plaats</Label>
              <Input
                value={settings.postalCity}
                onChange={(e) => setSettings({ ...settings, postalCity: e.target.value })}
                placeholder="1234AB Plaatsnaam"
              />
            </div>

            <div className="space-y-2">
              <Label>Telefoonnummer</Label>
              <Input
                value={settings.phone}
                onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                placeholder="+31 6 12345678"
              />
            </div>

            <div className="space-y-2">
              <Label>IBAN</Label>
              <Input
                value={settings.iban}
                onChange={(e) => setSettings({ ...settings, iban: e.target.value })}
                placeholder="NL00BANK0123456789"
              />
            </div>

            <div className="space-y-2">
              <Label>KVK-nummer</Label>
              <Input
                value={settings.kvk}
                onChange={(e) => setSettings({ ...settings, kvk: e.target.value })}
                placeholder="12345678"
              />
            </div>

            <div className="space-y-2">
              <Label>BTW-nummer</Label>
              <Input
                value={settings.btw}
                onChange={(e) => setSettings({ ...settings, btw: e.target.value })}
                placeholder="NL000000000B00"
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <Button
              onClick={handleSaveSettings}
              disabled={saving}
              className="bg-gradient-to-r from-blue-600 to-green-600"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Opslaan...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Instellingen Opslaan
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CompanySettings;
